import { useState, useEffect } from "react";
import { Star, Timer } from "lucide-react";

/**
 * Celebration overlay when a student finishes reading a text.
 * Shows Navi with golden stars, reading time, and congratulations.
 */
export default function CelebrationOverlay({ show, onClose, studentName, readingTime }) {
  const [stars, setStars] = useState([]);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setVisible(true);
      // Generate random star positions
      const newStars = Array.from({ length: 12 }, (_, i) => ({
        id: i,
        left: Math.random() * 80 + 10,
        top: Math.random() * 60 + 10,
        delay: Math.random() * 1.5,
        size: Math.random() * 20 + 16,
        rotation: Math.random() * 360,
      }));
      setStars(newStars);
    }
  }, [show]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  if (!show && !visible) return null;

  return (
    <div
      data-testid="celebration-overlay"
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-opacity duration-300 ${
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Stars animation */}
      {stars.map((star) => (
        <div
          key={star.id}
          className="absolute pointer-events-none"
          style={{
            left: `${star.left}%`,
            top: `${star.top}%`,
            animationDelay: `${star.delay}s`,
          }}
        >
          <Star
            size={star.size}
            className="text-yellow-400 fill-yellow-400 animate-bounce"
            style={{
              animationDelay: `${star.delay}s`,
              animationDuration: "1.5s",
              transform: `rotate(${star.rotation}deg)`,
              filter: "drop-shadow(0 0 8px rgba(250, 204, 21, 0.6))",
            }}
          />
        </div>
      ))}

      {/* Card */}
      <div
        className="relative z-10 bg-white rounded-3xl shadow-2xl p-8 md:p-10 max-w-sm mx-4 text-center page-enter"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Big star */}
        <div className="relative w-24 h-24 mx-auto mb-4">
          <Star
            size={96}
            className="text-yellow-400 fill-yellow-400 mx-auto"
            style={{ filter: "drop-shadow(0 4px 12px rgba(250, 204, 21, 0.5))" }}
          />
          <span className="absolute inset-0 flex items-center justify-center font-fredoka font-bold text-yellow-800 text-2xl">
            1
          </span>
        </div>

        {/* Mascot */}
        <img
          src="/LEcoleDeNavi4.png"
          alt="Navi"
          className="h-28 mx-auto mb-4 mascot-float"
        />

        {/* Message */}
        <h2 className="font-fredoka text-2xl md:text-3xl font-bold text-slate-800 mb-2">
          Bravo{studentName ? `, ${studentName}` : ""} !
        </h2>
        <p className="font-nunito text-base text-slate-500 mb-4">
          Tu as termine la lecture ! Navi est fier de toi !
        </p>

        {/* Reading time */}
        {readingTime > 0 && (
          <div data-testid="celebration-time" className="flex items-center justify-center gap-2 mb-4 px-4 py-2 bg-blue-50 rounded-xl">
            <Timer size={18} className="text-blue-500" />
            <span className="font-fredoka font-bold text-blue-700 text-lg">
              {Math.floor(readingTime / 60)}:{(readingTime % 60).toString().padStart(2, "0")}
            </span>
            <span className="font-nunito text-sm text-blue-400">de lecture</span>
          </div>
        )}

        {/* Stars earned */}
        <div className="flex items-center justify-center gap-1 mb-6">
          {[1, 2, 3].map((i) => (
            <Star
              key={i}
              size={28}
              className="text-yellow-400 fill-yellow-400"
              style={{
                animationDelay: `${i * 0.2}s`,
                filter: "drop-shadow(0 2px 4px rgba(250, 204, 21, 0.4))",
              }}
            />
          ))}
        </div>

        <button
          data-testid="celebration-close-btn"
          onClick={handleClose}
          className="w-full py-4 bg-orange-500 active:bg-orange-600 text-white font-fredoka font-bold text-lg rounded-full shadow-[0_4px_0_rgb(194,65,12)] btn-bouncy transition-all touch-target"
        >
          Super, on continue !
        </button>
      </div>
    </div>
  );
}
